/**
 * Talks to the generation worker, and keeps one puzzle warm.
 *
 * Two things the UI wants that the worker alone does not give it: a promise
 * instead of a message protocol, and a puzzle already built for the difficulty
 * the player is on. The prefetch is why the second "new puzzle" arrives
 * immediately — the first one paid for it.
 *
 * `?worker&inline` embeds the worker in this bundle rather than emitting a second
 * file. That keeps the served asset list to one script, and means the service
 * worker has nothing extra to cache for the game to work offline.
 */

import GenerationWorker from './worker?worker&inline';
import { generatePuzzle } from './generator';
import type { GenerateRequest, WorkerMessage } from './worker';
import type { Difficulty, Puzzle } from './types';

type Pending = {
  resolve: (puzzle: Puzzle) => void;
  reject: (error: Error) => void;
  onProgress?: (attempt: number) => void;
};

export class Generator {
  private worker: Worker | null = null;
  private readonly pending = new Map<number, Pending>();
  private nextId = 1;
  private prefetch: { difficulty: Difficulty; promise: Promise<Puzzle> } | null = null;

  /** Drops the worker mid-task, cancelling whatever it is searching for. */
  private discardWorker(): void {
    this.worker?.terminate();
    this.worker = null;
    for (const entry of this.pending.values()) entry.reject(new Error('cancelled'));
    this.pending.clear();
    this.prefetch = null;
  }

  private ensureWorker(): Worker | null {
    if (this.worker) return this.worker;
    try {
      const worker = new GenerationWorker();
      worker.addEventListener('message', (event: MessageEvent<WorkerMessage>) => {
        const message = event.data;
        const entry = this.pending.get(message.id);
        if (!entry) return;
        if (message.type === 'progress') {
          entry.onProgress?.(message.attempt);
          return;
        }
        this.pending.delete(message.id);
        if (message.type === 'ready') entry.resolve(message.puzzle);
        else entry.reject(new Error(message.message));
      });
      this.worker = worker;
      return worker;
    } catch {
      // No worker available: fall back to generating inline. The page will jank,
      // which beats not producing a puzzle.
      return null;
    }
  }

  private start(difficulty: Difficulty, onProgress?: (attempt: number) => void): Promise<Puzzle> {
    const worker = this.ensureWorker();
    if (!worker) return generatePuzzle({ difficulty });
    const id = this.nextId;
    this.nextId += 1;
    const request: GenerateRequest = { id, difficulty };
    return new Promise<Puzzle>((resolve, reject) => {
      this.pending.set(id, { resolve, reject, onProgress });
      worker.postMessage(request);
    });
  }

  /** A puzzle for this difficulty — the prefetched one if it fits. */
  request(difficulty: Difficulty, onProgress?: (attempt: number) => void): Promise<Puzzle> {
    if (this.prefetch?.difficulty === difficulty) {
      const ready = this.prefetch.promise;
      this.prefetch = null;
      return ready;
    }
    // A prefetch for another difficulty is now dead weight, and the worker is
    // single threaded: leaving it running would queue the puzzle the player is
    // actually waiting for behind a search they will never see.
    if (this.prefetch) this.discardWorker();
    return this.start(difficulty, onProgress);
  }

  /** Starts building the next puzzle at this difficulty, if none is waiting. */
  warm(difficulty: Difficulty): void {
    if (this.prefetch?.difficulty === difficulty) return;
    const promise = this.start(difficulty);
    // A rejected prefetch must not surface as an unhandled rejection; the real
    // request re-runs generation and reports the failure itself. That includes
    // the rejection `discardWorker` hands out when this prefetch is abandoned.
    promise.catch(() => undefined);
    this.prefetch = { difficulty, promise };
  }
}
